import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription, TimeoutError, combineLatest, of, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, timeout } from 'rxjs/operators';

import { LoginService } from '../auth/services/login.service';
import {
  ConnectApplyCompany,
  ConnectApplyOutcome,
  ConnectApplyProgress,
  ConnectApplyResult,
  ConnectApplyState
} from '../models/connect-apply.model';
import {
  clearConnectLoginPending,
  clearConnectPending,
  getConnectLoginPendingUser,
  isAuth0Session,
  isConnectReturnPending
} from './connect-apply.storage';
import { DataDogLoggerService } from './data-dog-logger.service';

/** Claves de la respuesta que significan "el usuario ganó/actualizó acceso a una empresa". */
const ADDED_KEYS = ['created', 'attached', 'restored', 'activated', 'promoted'];
/** Claves del resultado que implican que cambiaron las accounts del usuario. */
const CHANGE_KEYS = [...ADDED_KEYS, 'removed', 'disabled'];

/** Contexto de una corrida (un POST, o su reintento). */
interface CallContext {
  userId: number;
  /** La corrida vino de Agrology Connect (`#connect=1`): el POST lleva `from_connect: true`. */
  fromConnect: boolean;
  /** Es el reintento por `locked`. */
  retried: boolean;
}

/** Escapa HTML: toastr está configurado con `enableHtml: true`. */
export function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Aplica en Agree los cambios que el usuario hizo en Agrology Connect
 * (`POST /agrology/connect/apply` de api2): sumar/quitar al usuario de
 * empresas. No hay vínculo por code ni por email: api2 identifica al usuario
 * de Agrology por el `sub` del token Auth0 de la request.
 *
 * Cuándo corre (ver `start()` y `onSession()`), SOLO con sesión Auth0
 * (`isAuth0Session` del `access_token`):
 * - Una vez por LOGIN real Auth0: callback o popup de Auth0 (incluido el SSO
 *   silencioso) y cualquier otro login cuyo token sea de Auth0.
 *   `LoginService` deja la marca `connect_login_pending` (sessionStorage, con
 *   el id del usuario) en esos logins y emite `loginEvents$`; la marca se
 *   consume cuando termina la corrida, así un F5 posterior no vuelve a llamar
 *   pero un reload con el POST todavía en vuelo sí.
 * - Siempre que haya un `#connect=1` pendiente (el usuario vuelve de
 *   Connect), aunque la sesión venga restaurada del cache. Ese POST lleva
 *   `{from_connect: true}` (api2 no usa su caché negativa de `not_linked`).
 * - NUNCA en un arranque con sesión restaurada sin `connect=1` (F5, pestaña nueva).
 * - NUNCA con sesión legacy (Passport): las marcas pendientes se descartan
 *   sin llamar.
 *
 * Se engancha a `user$` + `token$` (y no solo a `user$`) porque `parseLogin`
 * emite el usuario ANTES que el token: con solo `user$` el request saldría sin
 * `Authorization`. `loginEvents$` hace que un login nuevo del MISMO usuario
 * (p.ej. `authenticateWithRedirectedToken` sobre una sesión ya abierta) también
 * cuente.
 *
 * Nunca interrumpe al usuario: cualquier error termina como `skipped`
 * silencioso. En particular NO pasa por `ErrorService` ni deja escapar el
 * error (el `DefaultErrorHandler` lo mandaría ahí): un 401 de este endpoint
 * haría `customLogout` y sacaría al usuario de la app por una tarea
 * secundaria. Si el token realmente venció, el próximo request normal se
 * encarga.
 *
 * Tiempos: el request espera hasta 60 s (el lock por usuario de api2 dura
 * 60 s); a los 15 s la UI avisa que tarda más de lo normal. Si igual vence,
 * el resultado es `unknown` (api2 puede seguir aplicando): se refrescan las
 * accounts a los 10 s y 30 s y no se muestra como error definitivo.
 */
@Injectable({ providedIn: 'root' })
export class ConnectApplyService implements OnDestroy {

  private static readonly URL = '/:apiBase/agrology/connect/apply';
  public static readonly TIMEOUT_MS = 60_000;
  public static readonly SLOW_MS = 15_000;
  public static readonly LOCKED_RETRY_MS = 5_000;
  public static readonly DEFERRED_REFRESH_MS: readonly number[] = [10_000, 30_000];

  private static readonly IDLE_PROGRESS: ConnectApplyProgress = { active: false, fromConnect: false, slow: false };

  private readonly applyState = new BehaviorSubject<ConnectApplyState>('idle');
  private readonly outcome = new BehaviorSubject<ConnectApplyOutcome | null>(null);
  private readonly progress = new BehaviorSubject<ConnectApplyProgress>(ConnectApplyService.IDLE_PROGRESS);

  private currentUserId: number | null = null;
  private inflight: Subscription;
  /** Timers de la corrida actual: aviso "lento", reintento por `locked`, refrescos diferidos. */
  private timers = new Subscription();
  private sessionSubs: Subscription;

  constructor(
    private http: HttpClient,
    private router: Router,
    private loginService: LoginService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private dataDogLoggerService: DataDogLoggerService
  ) { }

  /** Estado de la corrida actual. */
  public get applyState$(): Observable<ConnectApplyState> {
    return this.applyState.asObservable();
  }

  /** Progreso visible (`/companies` lo usa para el estado vacío). */
  public get progress$(): Observable<ConnectApplyProgress> {
    return this.progress.asObservable();
  }

  /** Resultado de la última corrida terminada (o `null`). */
  public get outcome$(): Observable<ConnectApplyOutcome | null> {
    return this.outcome.asObservable();
  }

  /** Último resultado terminado, sincrónico. */
  public get lastOutcome(): ConnectApplyOutcome | null {
    return this.outcome.value;
  }

  /** Se llama una sola vez, desde `AppComponent.ngOnInit`. */
  public start(): void {
    if (this.sessionSubs) return;

    this.sessionSubs = combineLatest([
      this.loginService.getCurrentUser(),
      this.loginService.getCurrentToken(),
      this.loginService.loginEvents$
    ]).pipe(
      // `parseLogin` hace `user.next` y después `token.next` en el mismo tick
      // (y `recordLogin` justo antes): se espera a que terminen todos (y a que
      // el interceptor tome el token nuevo, que también está suscripto a `token$`).
      debounceTime(0),
      map(([user, token, logins]) => ({
        userId: (user?.id && token?.access_token) ? user.id as number : null,
        accessToken: (token?.access_token as string) ?? null,
        logins
      })),
      // Un refresh de token no cambia nada; un login nuevo sí, aunque sea el
      // mismo usuario.
      distinctUntilChanged((a, b) => a.userId === b.userId && (a.userId === null || a.logins === b.logins))
    ).subscribe(({ userId, accessToken }) => this.onSession(userId, accessToken));
  }

  private onSession(userId: number | null, accessToken: string | null): void {
    this.inflight?.unsubscribe();
    this.inflight = undefined;
    this.clearTimers();
    this.currentUserId = userId;

    if (userId === null) {
      this.applyState.next('idle');
      this.progress.next(ConnectApplyService.IDLE_PROGRESS);
      this.outcome.next(null);
      return;
    }

    const fromConnect = isConnectReturnPending();

    // Marca de login: si es de otro usuario (quedó colgada), no aplica a esta
    // sesión y se descarta.
    const loginUser = getConnectLoginPendingUser();
    const afterLogin = loginUser === userId;
    if (loginUser !== null && !afterLogin) clearConnectLoginPending();

    // Sesión restaurada (F5, pestaña nueva) sin `connect=1`: no hay nada que aplicar.
    if (!fromConnect && !afterLogin) {
      this.applyState.next('idle');
      this.progress.next(ConnectApplyService.IDLE_PROGRESS);
      return;
    }

    // Sesión legacy (Passport): Connect solo funciona con Auth0. No se llama
    // nunca y las marcas pendientes se descartan (api2 respondería not_auth0).
    if (!isAuth0Session(accessToken)) {
      if (fromConnect) clearConnectPending();
      if (afterLogin) clearConnectLoginPending();
      this.applyState.next('idle');
      this.progress.next(ConnectApplyService.IDLE_PROGRESS);
      return;
    }

    this.call({ userId, fromConnect, retried: false });
  }

  private call(ctx: CallContext): void {
    this.inflight?.unsubscribe();
    this.clearTimers();
    this.applyState.next('running');
    this.progress.next({ active: true, fromConnect: ctx.fromConnect, slow: false });

    this.timers.add(timer(ConnectApplyService.SLOW_MS).subscribe(() => {
      if (ctx.userId === this.currentUserId && this.applyState.value === 'running') {
        this.progress.next({ ...this.progress.value, slow: true });
      }
    }));

    const body: { from_connect?: boolean } = ctx.fromConnect ? { from_connect: true } : {};

    let timedOut = false;
    this.inflight = this.http.post<unknown>(ConnectApplyService.URL, body).pipe(
      timeout(ConnectApplyService.TIMEOUT_MS),
      map(response => this.normalize(response)),
      catchError(error => {
        timedOut = error instanceof TimeoutError || error?.name === 'TimeoutError';
        this.dataDogLoggerService.warn('connect.apply.error', {
          status: error instanceof HttpErrorResponse ? error.status : undefined,
          name: error?.name,
          timed_out: timedOut,
          from_connect: ctx.fromConnect,
          retried: ctx.retried
        });
        return of(null);
      })
    ).subscribe(result => {
      try {
        this.handle(ctx, result, timedOut);
      } catch (e) {
        // Nunca dejar escapar: el DefaultErrorHandler lo mandaría a ErrorService.
        this.dataDogLoggerService.warn('connect.apply.handle_error', { message: e?.message });
        this.finish(ctx.userId, ctx.fromConnect, 'failed', null);
      }
    });
  }

  private handle(ctx: CallContext, result: ConnectApplyResult | null, timedOut: boolean): void {
    // Respuesta de una sesión que ya no está (logout/cambio de usuario en el medio).
    if (ctx.userId !== this.currentUserId) return;
    this.clearTimers();

    // El apply de este login ya corrió (con cualquier resultado): un F5 o una
    // pestaña restaurada no lo repiten. Si la marca es de otro login posterior
    // (otro usuario), no se toca.
    if (getConnectLoginPendingUser() === ctx.userId) clearConnectLoginPending();

    // Cualquier respuesta (o error/timeout) consume la marca `connect=1`: el
    // reintento por `locked` la lleva en su contexto, y un F5 posterior no
    // tiene por qué repetir el apply.
    if (ctx.fromConnect) clearConnectPending();

    if (timedOut) {
      this.finishUnknown(ctx, null);
      return;
    }

    if (!result) {
      this.finish(ctx.userId, ctx.fromConnect, 'failed', null);
      return;
    }

    if (result.status === 'skipped' && result.reason === 'locked') {
      // Otro apply del mismo usuario tiene el lock (otra pestaña o un
      // reload). Un solo reintento, con el mismo `from_connect`.
      if (!ctx.retried) {
        this.timers.add(timer(ConnectApplyService.LOCKED_RETRY_MS).subscribe(() => {
          if (ctx.userId !== this.currentUserId) return;
          this.call({ ...ctx, retried: true });
        }));
        return;
      }
      this.finishUnknown(ctx, result);
      return;
    }

    const failed = result.status === 'skipped' && result.reason === 'service_unavailable';
    this.finish(ctx.userId, ctx.fromConnect, failed ? 'failed' : 'done', result);

    // Venía de Connect pero api2 no encontró su cuenta de Connect (otra
    // identidad Auth0, o nunca abrió /connect): aviso claro, sin bloquear.
    // Sin `from_connect` (login normal) es lo esperable y no se muestra nada;
    // `not_auth0` tampoco.
    if (ctx.fromConnect && result.status === 'skipped' && result.reason === 'not_linked') {
      this.toastrService.info(this.translateService.instant('CONNECT.NOT_LINKED'), undefined, {
        disableTimeOut: true,
        closeButton: true
      });
    }

    if (result.status === 'applied' || result.status === 'partial') {
      this.loginService.refreshAccounts();
      // `user.accounts` (del login cacheado) no se entera solo de las empresas
      // que sumó/quitó Connect: sin esto los cupos mandan `account` vacío.
      // api2 manda `accounts` (forma cruda del login) cuando hubo cambios.
      if (CHANGE_KEYS.some(key => (result[key] as ConnectApplyCompany[] | undefined)?.length > 0)) {
        this.loginService.applyUserAccounts(ctx.userId, result.accounts);
      }
      this.leaveRemovedCompany(result);
      this.notifyChanges(result);
    }
  }

  private finish(userId: number, fromConnect: boolean, state: ConnectApplyState, result: ConnectApplyResult | null): void {
    const added = !!result && ADDED_KEYS
      .some(key => (result[key] as ConnectApplyCompany[] | undefined)?.length > 0);

    this.progress.next(ConnectApplyService.IDLE_PROGRESS);
    this.outcome.next({ userId, fromConnect, state, result, addedCompanies: added });
    this.applyState.next(state);
  }

  /**
   * Resultado desconocido: api2 puede seguir aplicando bajo su lock. Se
   * refrescan las accounts más tarde; mientras tanto (si venía de Connect) el
   * estado vacío sigue mostrando "tarda más de lo normal".
   */
  private finishUnknown(ctx: CallContext, result: ConnectApplyResult | null): void {
    this.finish(ctx.userId, ctx.fromConnect, 'unknown', result);
    this.progress.next({ active: true, fromConnect: ctx.fromConnect, slow: true });

    const delays = ConnectApplyService.DEFERRED_REFRESH_MS;
    const last = Math.max(...delays);
    delays.forEach(delay => {
      this.timers.add(timer(delay).subscribe(() => {
        if (ctx.userId !== this.currentUserId) return;
        this.loginService.refreshAccounts();
        if (delay === last) this.progress.next(ConnectApplyService.IDLE_PROGRESS);
      }));
    });
  }

  private clearTimers(): void {
    this.timers.unsubscribe();
    this.timers = new Subscription();
  }

  /**
   * Acepta la forma plana del contrato y, por las dudas, un envoltorio JSend
   * (`{status: 'success', data: {...}}`). Cualquier otra cosa = `null` (error).
   */
  private normalize(response: any): ConnectApplyResult | null {
    let body = response;
    if (body?.status === 'success' && body.data && typeof body.data === 'object') body = body.data;

    const statuses = ['noop', 'applied', 'partial', 'skipped'];
    if (!body || !statuses.includes(body.status)) return null;

    return body as ConnectApplyResult;
  }

  /** Toasts de lo que cambió: empresas sumadas/actualizadas y fallos parciales. */
  private notifyChanges(result: ConnectApplyResult): void {
    const added = ADDED_KEYS
      .reduce((count, key) => count + ((result[key] as ConnectApplyCompany[] | undefined)?.length || 0), 0);

    if (added > 0) {
      this.toastrService.success(this.translateService.instant(
        added === 1 ? 'CONNECT.ADDED_ONE' : 'CONNECT.ADDED_MANY', { count: added }
      ));
    }

    const failed = result.failed?.length || 0;
    if (failed > 0) {
      this.toastrService.warning(this.translateService.instant(
        failed === 1 ? 'CONNECT.FAILED_ONE' : 'CONNECT.FAILED_MANY', { count: failed }
      ), undefined, { disableTimeOut: true, closeButton: true });
    }
  }

  /**
   * Si el usuario está parado dentro de una empresa de la que Connect lo
   * acaba de quitar (o que quedó inhabilitada), se lo lleva al listado.
   */
  private leaveRemovedCompany(result: ConnectApplyResult): void {
    const gone = [...(result.removed || []), ...(result.disabled || [])];
    if (!gone.length) return;

    const match = this.router.url.match(/^\/company\/(\d+)(?:[/?#]|$)/);
    if (!match) return;

    const current = gone.find(company => String(company.company_id) === match[1]);
    if (!current) return;

    this.router.navigate(['/companies']).then(() => {
      // La razón social viene de afuera y toastr interpreta HTML.
      this.toastrService.info(this.translateService.instant('CONNECT.REMOVED_CURRENT', {
        name: escapeHtml(current.razon_social || '')
      }));
    });
  }

  ngOnDestroy(): void {
    this.inflight?.unsubscribe();
    this.clearTimers();
    this.sessionSubs?.unsubscribe();
  }
}
